/*
 * The drive's keys (S-01 §3.2), registered into the one shortcut registry through the `shortcuts` slot,
 * which is what puts them in the keyboard sheet: an undocumented shortcut cannot exist
 * (`docs/web/keyboard.md`).
 *
 * One scope, `drive`, which a page of the drive pushes while it is on route — Recent, a space, a
 * folder, Starred, the trash. **New** creates in the place on screen: a document `Shift+N`, a folder
 * `Shift+F`, an upload `Shift+U`. A list's row under the focus is renamed `F2`, moved `V`, starred `S`
 * and opened in Details `I`. The document page is not in this scope: there the keys are the text's.
 *
 * A definition is not a behaviour: the registry is built once per boot, while what "the place on
 * screen" and "the row under the focus" are depends on the page. Each binding calls the controller the
 * open page installs; with no drive page open the scope is not on the stack, and a key that arrived
 * anyway does nothing.
 */

import type { ShortcutDef, ShortcutScopeDef } from "../../core/modules/registry.ts"

/** What an open page of the drive lends its keys. */
export interface DrivePageController {
	/** `Shift+N`: a new document in the place on screen, opened with the caret in its title. */
	newDocument(): void
	/** `Shift+F`: a new folder in the place on screen. */
	newFolder(): void
	/** `Shift+U`: the file picker, uploading into the place on screen. */
	uploadFiles(): void
	/** `F2`: rename the row under the focus. */
	rename(): void
	/** `V`: move the row under the focus. */
	moveTo(): void
	/** `S`: star or unstar the row under the focus. */
	toggleStar(): void
	/** `I`: open Details of the row under the focus. */
	details(): void
	/** `J`: the focus to the next row of the list; a page without a list leaves it out. */
	next?(): void
	/** `K`: the focus to the previous row of the list. */
	previous?(): void
	/** `Delete`: move the row under the focus to the trash. */
	trash?(): void
}

let page: DrivePageController | null = null

/** Installed by a page of the drive while it is mounted; cleared when it is not. */
export function setDrivePageController(next: DrivePageController | null): void {
	page = next
}

/** The scope a page of the drive pushes while it is on route. */
export const DRIVE_SCOPE = "drive"

export const driveShortcutScopes: ShortcutScopeDef[] = [
	{ scope: DRIVE_SCOPE, titleKey: "drive.title", stack: ["global", DRIVE_SCOPE] },
]

export const driveShortcuts: ShortcutDef[] = [
	{
		id: "drive.newDocument",
		keys: ["Shift+N"],
		scope: DRIVE_SCOPE,
		labelKey: "drive.new.document",
		run: () => page?.newDocument(),
	},
	{
		id: "drive.newFolder",
		keys: ["Shift+F"],
		scope: DRIVE_SCOPE,
		labelKey: "drive.new.folder",
		run: () => page?.newFolder(),
	},
	{
		id: "drive.upload",
		keys: ["Shift+U"],
		scope: DRIVE_SCOPE,
		labelKey: "drive.new.fileUpload",
		run: () => page?.uploadFiles(),
	},
	{
		id: "drive.rename",
		keys: ["F2"],
		scope: DRIVE_SCOPE,
		labelKey: "drive.item.rename",
		run: () => page?.rename(),
	},
	{
		id: "drive.moveTo",
		keys: ["v"],
		scope: DRIVE_SCOPE,
		labelKey: "drive.item.moveTo",
		run: () => page?.moveTo(),
	},
	{
		id: "drive.star",
		keys: ["s"],
		scope: DRIVE_SCOPE,
		labelKey: "drive.item.star",
		run: () => page?.toggleStar(),
	},
	{
		id: "drive.details",
		keys: ["i"],
		scope: DRIVE_SCOPE,
		labelKey: "drive.item.details",
		run: () => page?.details(),
	},
	{
		id: "drive.next",
		keys: ["j"],
		scope: DRIVE_SCOPE,
		labelKey: "drive.shortcut.nextRow",
		run: () => page?.next?.(),
	},
	{
		id: "drive.previous",
		keys: ["k"],
		scope: DRIVE_SCOPE,
		labelKey: "drive.shortcut.previousRow",
		run: () => page?.previous?.(),
	},
	{
		id: "drive.trash",
		keys: ["Delete"],
		scope: DRIVE_SCOPE,
		labelKey: "drive.item.trash",
		run: () => page?.trash?.(),
	},
]
